import { FormProvider, useForm, useWatch } from "react-hook-form";
import React from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { PdFieldset, PdFormLabel } from "src/components/form-control";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { tPdInputProps } from "src/components/forms/input";
import { PdRadio } from "src/components/forms/radio";
import { tPdSelect } from "src/components/forms/select";
import {
  PdHookFormErrorMessage,
  PdHookFormInput,
  PdHookFormInputPercentage,
  PdHookFormInputPhone,
  PdHookFormRadioGroup,
  PdHookFormSelect,
} from "src/components/hook-form";
import { PdStack, PdText } from "src/components/layout";
import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { useAppDispatch } from "src/redux";
import { tModalComponentProps } from "src/redux/modals/types";
import {
  caProvinceOptions,
  retailerCategoryOptions,
  retailerLocationQuantityOptions,
  retailerPricePointOptions,
  usStateOptions,
} from "src/util/comboBoxData";
import { kEmailRegex } from "src/util/constants";
import {
  tPrice,
  tRetailerApplication,
  tRetailerCategory,
  tRetailLocationQuantity,
} from "tmt_api/src/types/types.validator";
import { useIsMobile } from "../ui";
import { getSafeUrl } from "src/util/functions";
import { formatPercentNumber } from "src/util/gFormatting";
import { brandAddRetailer, repAddRetailer } from "src/redux/company/actions";
import { useConfirmModal } from ".";
import { getApiError } from "src/util/apiNew";

type tModalParams = {
  companyId: string;
  brandDefaultShippingFee?: number;
  source: "brand" | "rep";
};

type tCountryCodes = "US" | "CA";

type tOptionFormData<T extends unknown = string> = { label: string; value: T } | null;

type tRetailerFormData = {
  isTradeshowCustomer: string;
  tradeshowDetails: string;
  storeName: string;
  website: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  facebook: string;
  instagram: string;
  country: tOptionFormData<tCountryCodes>;
  address1: string;
  address2: string;
  city: string;
  state: tOptionFormData;
  zipCode: string;
  category: tOptionFormData<tRetailerCategory>;
  price: tOptionFormData<tPrice>;
  locationQuantity: tOptionFormData<tRetailLocationQuantity>;
  groundShippingFee?: number | null;
};

const usCountryOption: tOptionFormData<tCountryCodes> = { label: "United States", value: "US" };
const caCountryOption: tOptionFormData<tCountryCodes> = { label: "Canada", value: "CA" };

const countryOptions = [usCountryOption, caCountryOption];

const USStatesSelect: React.FC<{ onSubmitEditing?: tPdSelect["onSubmitEditing"] }> = ({ onSubmitEditing }) => {
  return (
    <>
      <PdFormLabel>State *</PdFormLabel>
      <PdHookFormSelect
        name="state"
        rules={{ required: "Required" }}
        placeholder="State"
        options={usStateOptions}
        isClearable={false}
        onSubmitEditing={onSubmitEditing}
        direction="top"
      />
      <PdHookFormErrorMessage name="state" />
    </>
  );
};

const CAProvincesSelect: React.FC<{ onSubmitEditing?: tPdSelect["onSubmitEditing"] }> = ({ onSubmitEditing }) => {
  return (
    <>
      <PdFormLabel>Province *</PdFormLabel>
      <PdHookFormSelect
        name="state"
        rules={{ required: "Required" }}
        placeholder="Province"
        options={caProvinceOptions}
        isClearable={false}
        onSubmitEditing={onSubmitEditing}
        direction="top"
      />
      <PdHookFormErrorMessage name="state" />
    </>
  );
};

const ZipCodeInput: React.FC<{ onSubmitEditing?: tPdInputProps["onSubmitEditing"] }> = ({ onSubmitEditing }) => {
  return (
    <>
      <PdFormLabel>Zip *</PdFormLabel>
      <PdHookFormInput
        name="zipCode"
        rules={{ required: "Required", pattern: { value: /\d{5}/, message: "Enter 5 digit zipcode" } }}
        placeholder="100001"
        onSubmitEditing={onSubmitEditing}
      />
      <PdHookFormErrorMessage name="zipCode" />
    </>
  );
};

const PostalCodeInput: React.FC<{ onSubmitEditing?: tPdInputProps["onSubmitEditing"] }> = ({ onSubmitEditing }) => {
  return (
    <>
      <PdFormLabel>Postal Code *</PdFormLabel>
      <PdHookFormInput
        name="zipCode"
        rules={{ required: "Required" }}
        placeholder="Postal Code"
        onSubmitEditing={onSubmitEditing}
      />
      <PdHookFormErrorMessage name="zipCode" />
    </>
  );
};

const AddRetailerModal: React.FC<tModalComponentProps<tModalParams>> = ({
  onClose,
  onResolve,
  companyId,
  brandDefaultShippingFee,
  source,
}) => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const confirmModal = useConfirmModal();
  const formMethods = useForm<tRetailerFormData>({
    defaultValues: {
      isTradeshowCustomer: "",
      tradeshowDetails: "",
      storeName: "",
      website: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      facebook: "",
      instagram: "",
      country: usCountryOption,
      address1: "",
      address2: "",
      city: "",
      state: null,
      zipCode: "",
      category: null,
      price: null,
      locationQuantity: null,
      groundShippingFee: brandDefaultShippingFee ? formatPercentNumber(brandDefaultShippingFee) : null,
    },
  });
  const [isTradeshowCustomer, country] = useWatch({
    name: ["isTradeshowCustomer", "country"],
    control: formMethods.control,
  });
  const [isSubmitting, setSubmitting] = React.useState(false);

  const handleOnClose = () => {
    onClose();
  };

  const handleOnSubmit = formMethods.handleSubmit(async (formData) => {
    try {
      setSubmitting(true);
      const payload: tRetailerApplication = {
        contact: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
        },
        type: "retailer",
        name: formData.storeName,
        address: {
          first_name: "",
          last_name: "",
          company: "",
          address1: formData.address1,
          address2: formData.address2,
          city: formData.city,
          state_or_province: formData.state?.value ?? "",
          postal_code: formData.zipCode,
          country_code: formData.country?.value ?? "",
          phone: formData.phone,
        },
        website: getSafeUrl(formData.website),
        socialMedia: {
          facebook: getSafeUrl(formData.facebook),
          instagram: getSafeUrl(formData.instagram),
        },
        category: formData.category?.value ?? retailerCategoryOptions[0].value,
        price: formData.price?.value ?? retailerPricePointOptions[0].value,
        locationQuantity: formData.locationQuantity?.value ?? retailerLocationQuantityOptions[0].value,
        note: "",
        isTradeshowCustomer: formData.isTradeshowCustomer === "true",
        tradeshowDetails: formData.tradeshowDetails,
        groundShippingFee: (formData.groundShippingFee || 0) / 100,
      };

      let response: {
        isExistingUser: boolean;
        isConnected: boolean;
      };
      if (source === "brand") {
        response = await dispatch(brandAddRetailer(companyId, payload));
      } else {
        response = await dispatch(repAddRetailer(companyId, payload));
      }

      if (response.isConnected) {
        confirmModal.openAsync({
          title: "You're connected!",
          description:
            "Looks like you and this retailer are already connected; you can both communicate & transact on The Main Tab!",
          confirmButtonLabel: "OK",
          hideCancelButton: true,
        });
      } else if (response.isExistingUser) {
        confirmModal.openAsync({
          title: "Existing Retailer",
          description: () => (
            <>
              <PdText style={styles.mb4}>This Retailer has an active account with The Main Tab!</PdText>
              <PdText style={styles.mb10}>
                You are now connected and able to communicate and transact via our marketplace!
              </PdText>
            </>
          ),
          confirmButtonLabel: "OK",
          hideCancelButton: true,
        });
      } else {
        dispatch({ type: "RIBBON", message: "New Retailer added and welcome e-mail sent to Retailer!" });
      }

      onResolve();
    } catch (e) {
      setSubmitting(false);
      dispatch({ type: "RIBBON", message: new Error(getApiError(e).message) });
    }
  });

  return (
    <PdModal closeOnOverlayClick={!isSubmitting} style={styles.modal}>
      <PdModalCloseButton isDisabled={isSubmitting} onPress={handleOnClose} />
      <ScrollView contentContainerStyle={styles.modalBody}>
        <PdModalTitle style={styles.mb5}>Add a Retailer</PdModalTitle>
        <FormProvider {...formMethods}>
          <View style={[styles.widthFull, styles.mb3]}>
            <PdFieldset>Are you adding this retailer at a Tradeshow? *</PdFieldset>
            <PdHookFormRadioGroup
              name="isTradeshowCustomer"
              rules={{ required: "Required" }}
              onChange={() => {
                formMethods.setValue("tradeshowDetails", "");
              }}
            >
              <PdStack direction="horizontal">
                <PdRadio value="true">Yes</PdRadio>
                <PdRadio value="false">No</PdRadio>
              </PdStack>
            </PdHookFormRadioGroup>
            <PdHookFormErrorMessage name="isTradeshowCustomer" />
          </View>

          {isTradeshowCustomer === "true" && (
            <View style={[styles.widthFull, styles.mb3]}>
              <PdFormLabel>Trade Show Details</PdFormLabel>
              <PdHookFormInput
                name="tradeshowDetails"
                placeholder="Enter show name and date, example: New York-Feb 2023"
                onSubmitEditing={handleOnSubmit}
              />
            </View>
          )}

          <PdStack
            direction={isMobile ? "vertical" : "horizontal"}
            layout={[1, 1]}
            gap={3}
            style={[styles.widthFull, styles.mb3]}
          >
            <View>
              <PdFormLabel>Store Name *</PdFormLabel>
              <PdHookFormInput name="storeName" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
              <PdHookFormErrorMessage name="storeName" />
            </View>
            <View>
              <PdFormLabel>Website *</PdFormLabel>
              <PdHookFormInput
                name="website"
                rules={{ required: "Required" }}
                onSubmitEditing={handleOnSubmit}
                placeholder="www.website.com"
              />
              <PdHookFormErrorMessage name="website" />
            </View>
          </PdStack>

          <PdStack
            style={[styles.widthFull, styles.mb3]}
            direction={isMobile ? "vertical" : "horizontal"}
            layout={[1, 1]}
            gap={3}
          >
            <View>
              <PdFormLabel>First Name *</PdFormLabel>
              <PdHookFormInput name="firstName" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
              <PdHookFormErrorMessage name="firstName" />
            </View>
            <View>
              <PdFormLabel>Last Name *</PdFormLabel>
              <PdHookFormInput name="lastName" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
              <PdHookFormErrorMessage name="lastName" />
            </View>
          </PdStack>

          <PdStack
            style={[styles.widthFull, styles.mb3]}
            direction={isMobile ? "vertical" : "horizontal"}
            layout={[1, 1]}
            gap={3}
          >
            <View>
              <PdFormLabel>Email *</PdFormLabel>
              <PdHookFormInput
                name="email"
                rules={{ required: "Required", pattern: { value: kEmailRegex, message: "Invalid email" } }}
                onSubmitEditing={handleOnSubmit}
              />
              <PdHookFormErrorMessage name="email" />
            </View>
            <View>
              <PdFormLabel>Phone Number *</PdFormLabel>
              <PdHookFormInputPhone
                name="phone"
                placeholder="(XXX) XXX-XXXX"
                autoCompleteType="tel"
                rules={{ required: "Required" }}
                onSubmitEditing={handleOnSubmit}
              />
              <PdHookFormErrorMessage name="phone" />
            </View>
          </PdStack>

          <PdStack
            direction={isMobile ? "vertical" : "horizontal"}
            layout={[1, 1]}
            gap={3}
            style={[styles.widthFull, styles.mb3]}
          >
            <View>
              <PdFormLabel>Facebook</PdFormLabel>
              <PdHookFormInput
                name="facebook"
                onSubmitEditing={handleOnSubmit}
                placeholder="www.facebook.com/storename"
              />
              <PdHookFormErrorMessage name="facebook" />
            </View>
            <View>
              <PdFormLabel>Instagram</PdFormLabel>
              <PdHookFormInput
                name="instagram"
                onSubmitEditing={handleOnSubmit}
                placeholder="www.instagram.com/storename"
              />
              <PdHookFormErrorMessage name="instagram" />
            </View>
          </PdStack>

          <View style={[styles.zIndex1, styles.widthFull, styles.mb3]}>
            <PdFormLabel>Country *</PdFormLabel>
            <PdHookFormSelect
              name="country"
              rules={{ required: "Required" }}
              placeholder="United States"
              options={countryOptions}
              isClearable={false}
              onSubmitEditing={handleOnSubmit}
              onChange={() => {
                formMethods.setValue("address1", "");
                formMethods.setValue("address2", "");
                formMethods.setValue("city", "");
                formMethods.setValue("state", null);
                formMethods.setValue("zipCode", "");
              }}
            />
            <PdHookFormErrorMessage name="country" />
          </View>

          <PdStack
            style={[styles.widthFull, styles.mb3]}
            direction={isMobile ? "vertical" : "horizontal"}
            layout={[1, 1]}
            gap={3}
          >
            <View>
              <PdFormLabel>Address *</PdFormLabel>
              <PdHookFormInput name="address1" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
              <PdHookFormErrorMessage name="address1" />
            </View>
            <View>
              <PdFormLabel>Address 2</PdFormLabel>
              <PdHookFormInput name="address2" placeholder="Suite 1000" onSubmitEditing={handleOnSubmit} />
              <PdHookFormErrorMessage name="address2" />
            </View>
          </PdStack>

          <PdStack
            direction={isMobile ? "vertical" : "horizontal"}
            layout={[2, 2]}
            gap={3}
            style={[styles.widthFull, styles.mb3, styles.zIndex1]}
          >
            <View>
              <PdFormLabel>City *</PdFormLabel>
              <PdHookFormInput
                name="city"
                rules={{ required: "Required" }}
                placeholder="City"
                onSubmitEditing={handleOnSubmit}
              />
              <PdHookFormErrorMessage name="city" />
            </View>
            <View>
              {country?.value === "US" && <USStatesSelect onSubmitEditing={handleOnSubmit} />}
              {country?.value === "CA" && <CAProvincesSelect onSubmitEditing={handleOnSubmit} />}
            </View>
          </PdStack>

          <View style={[styles.widthFull, styles.mb3]}>
            {country?.value === "US" && <ZipCodeInput onSubmitEditing={handleOnSubmit} />}
            {country?.value === "CA" && <PostalCodeInput onSubmitEditing={handleOnSubmit} />}
          </View>

          <View style={[styles.widthFull, styles.mb3, styles.zIndex1]}>
            <PdFormLabel>What category best describes your store? *</PdFormLabel>
            <PdHookFormSelect
              name="category"
              rules={{ required: "Required" }}
              placeholder="E.g. Gift Shop"
              options={retailerCategoryOptions.sort((c1, c2) => c1.label.localeCompare(c2.label))}
              isClearable={false}
              onSubmitEditing={handleOnSubmit}
              direction="top"
            />
            <PdHookFormErrorMessage name="category" />
          </View>

          <View style={[styles.widthFull, styles.mb3, styles.zIndex1]}>
            <PdFormLabel>Average retail price point of your Brands *</PdFormLabel>
            <PdHookFormSelect
              name="price"
              rules={{ required: "Required" }}
              placeholder="Please select a price point"
              options={retailerPricePointOptions}
              isClearable={false}
              onSubmitEditing={handleOnSubmit}
              direction="top"
            />
            <PdHookFormErrorMessage name="price" />
          </View>

          <View style={[styles.widthFull, styles.mb3, styles.zIndex1]}>
            <PdFormLabel>How many Retail Locations do you have? *</PdFormLabel>
            <PdHookFormSelect
              name="locationQuantity"
              rules={{ required: "Required" }}
              placeholder="Number Including E-Commerce Site"
              options={retailerLocationQuantityOptions}
              isClearable={false}
              onSubmitEditing={handleOnSubmit}
              direction="top"
            />
            <PdHookFormErrorMessage name="locationQuantity" />
          </View>

          <View style={[styles.widthFull, styles.mb3, styles.zIndex1]}>
            <PdFormLabel>Ground Shipping *</PdFormLabel>
            <View style={styles.pdInputGroup}>
              <PdHookFormInputPercentage
                name="groundShippingFee"
                rules={{ required: "Required" }}
                onSubmitEditing={handleOnSubmit}
                style={[styles.widthFull, styles.border0]}
              />
            </View>
            <PdHookFormErrorMessage name="groundShippingFee" />
          </View>

          <PdStack
            isReversed={isMobile}
            direction={isMobile ? "vertical" : "horizontal"}
            layout={[1, 1]}
            gap={3}
            style={[styles.widthFull, styles._zIndex1, styles.mt3]}
          >
            <PdButton colorScheme="purple" isDisabled={isSubmitting} onPress={handleOnClose} variant="outline">
              <PdButtonLabel>Cancel</PdButtonLabel>
            </PdButton>
            <PdButton isLoading={isSubmitting} onPress={handleOnSubmit}>
              <PdButtonLabel>Save</PdButtonLabel>
            </PdButton>
          </PdStack>
        </FormProvider>
      </ScrollView>
    </PdModal>
  );
};

const useAddRetailerModal = (): tUseModal<tModalParams> => {
  return useModal(AddRetailerModal);
};

export default useAddRetailerModal;

const styles = StyleSheet.create({
  _zIndex1: {
    zIndex: -1,
  },
  border0: {
    borderWidth: 0,
  },
  mb10: {
    marginBottom: 10 * kStyleConst.kSpacing,
  },
  mb3: {
    marginBottom: kStyleConst.kSpacing * 3,
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  mb5: {
    marginBottom: kStyleConst.kSpacing * 5,
  },
  modal: {
    marginHorizontal: 8 * kStyleConst.kSpacing,
    maxWidth: 650,
    width: "100%",
  },
  modalBody: {
    alignItems: "flex-start",
    backgroundColor: kColors.lightBrand,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    paddingVertical: 8 * kStyleConst.kSpacing,
  },
  mt3: {
    marginTop: kStyleConst.kSpacing * 3,
  },
  pdInputGroup: {
    alignItems: "center",
    borderColor: kColors.gray300,
    borderWidth: 1,
    flexDirection: "row",
  },
  widthFull: {
    width: "100%",
  },
  zIndex1: {
    zIndex: 1,
  },
});
